import { SkeletonZeilen } from '../components/common/States'
import { useLinsenRegeln } from '../hooks/useQualyz'
import { LENSES, type LensKey } from '../lib/lenses'
import '../styles/aktie.css'

/**
 * Glossar fuer Kunden (Wunsch Kim, 12.09.2026).
 *
 * Bewusst NUR Definitionen, keine Ergebnisse aus dem Rueckvergleich: Der ist
 * explorativ und belegt nichts. Eine Aussage wie "Lynch wirkt bei kleinen
 * Werten" stand im Entwurf -- und der Groessen-Split hat genau sie widerlegt.
 */
const LINSEN_TEXT: Record<LensKey, { sub: string; text: string; strong: string }> = {
  quality: {
    sub: 'Bilanzqualität und Bewertung',
    text: 'Ein gesundes, vernünftig bewertetes Unternehmen: PEG, Free-Cashflow-Rendite, Umsatzwachstum, Bruttomarge und Verschuldung zusammengefasst.',
    strong: 'solide Bilanz zu einem vernünftigen Preis.',
  },
  momentum: {
    sub: 'Kurstrend',
    text: '12-1-Momentum: die Kursentwicklung der letzten zwölf Monate ohne den jüngsten Monat, der oft zurückprallt.',
    strong: 'anhaltender Aufwärtstrend.',
  },
  epv: {
    sub: 'Ertragswert',
    text: 'Earnings Power Value: was das Unternehmen allein aus seiner heutigen Ertragskraft wert ist, ganz ohne Wachstumsannahme. Verglichen mit dem Kurs.',
    strong: 'Kurs deutlich unter dem nüchternen Ertragswert.',
  },
  contrarian: {
    sub: 'abgestürzt, aber drehend',
    text: 'Tiefer Rückgang vom Höchststand, während die Fundamentaldaten kehren: Free-Cashflow-Wende, steigende Bruttomarge, hoher Piotroski-Score.',
    strong: 'stark gefallen und fundamental im Aufschwung.',
  },
  lynch: {
    sub: 'Wachstum zum fairen Preis',
    text: 'Der Ansatz von Peter Lynch: PEG, Kapitalrendite (ROIC), Unternehmensgröße und Beschleunigung des Umsatzwachstums.',
    strong: 'Wachstum, das man nicht zu teuer bezahlt.',
  },
  insider: {
    sub: 'Smart Money',
    text: 'Echte Insiderkäufe am Markt, keine Aktienzuteilungen an Manager. Besonders aussagekräftig, wenn mehrere Insider im selben Quartal kaufen. Ergänzt um das Kursziel der Analysten.',
    strong: 'mehrere Insider setzen eigenes Geld ein.',
  },
  sentiment: {
    sub: 'Analysten-Wende',
    text: 'Nicht das Niveau der Analystenurteile zählt, sondern die Wende: Werden die Urteile besser?',
    strong: 'die Stimmung dreht spürbar nach oben.',
  },
}

/**
 * Kennzahlen, nach Themen geordnet (Wunsch der Testpersonen ueber Kim,
 * 28.09.2026).
 *
 * Zu jeder Zahl steht, WANN SIE TAUGT UND WANN NICHT. Eine Definition allein
 * hilft niemandem: Das KUV ist bei einer Bank etwas anderes als bei einem
 * Softwarehaus, und ein KBV von 34 bei Apple ist kein Datenfehler, sondern
 * die Folge der Rueckkaeufe. Wer das nicht dazuschreibt, erzeugt Vertrauen
 * in Zahlen, die es nicht verdienen.
 */
interface Begriff {
  begriff: string
  text: string
  /** Wann die Zahl trägt und wann sie in die Irre führt. */
  taugt?: string
}

const GRUPPEN: { titel: string; einleitung?: string; begriffe: Begriff[] }[] = [
  {
    titel: 'Bewertung: Was kostet das Unternehmen?',
    einleitung:
      'Alle diese Zahlen setzen den Börsenwert ins Verhältnis zu etwas, das das Unternehmen erwirtschaftet oder besitzt. Keine davon ist für sich genommen ein Urteil — sie beantworten nur verschiedene Fassungen derselben Frage.',
    begriffe: [
      {
        begriff: 'KGV — Kurs-Gewinn-Verhältnis',
        text: 'Aktienkurs geteilt durch den Gewinn je Aktie. Die Zahl sagt, wie viele Jahresgewinne man für die Aktie bezahlt. Ein KGV von 20 heißt: zwanzig Jahre dieses Gewinns, wenn er gleich bliebe.',
        taugt: 'Die gebräuchlichste Kennzahl, und die mit den meisten Fallstricken. Bei Verlust gibt es keins. Bei einem Einmalgewinn — etwa dem Verkauf einer Sparte — sieht sie viel zu günstig aus. Bei stark schwankenden Gewinnen sagt ein einzelnes Jahr wenig.',
      },
      {
        begriff: 'KUV — Kurs-Umsatz-Verhältnis',
        text: 'Börsenwert geteilt durch den Jahresumsatz.',
        taugt: 'Taugt da, wo das KGV versagt: bei Verlustjahren und bei jungen Unternehmen, die noch nicht verdienen. Der Umsatz ist immer positiv und schwankt weniger als der Gewinn. Dafür ignoriert er völlig, ob am Umsatz etwas hängen bleibt — ein Händler mit 2 % Marge und ein Softwarehaus mit 30 % sind damit nicht vergleichbar.',
      },
      {
        begriff: 'KBV — Kurs-Buchwert-Verhältnis',
        text: 'Börsenwert geteilt durch das Eigenkapital. Wie viel man für einen Euro bilanzielles Vermögen zahlt.',
        taugt: 'Die wichtigste Zahl bei Banken und Versicherern, deren Bilanz das Geschäft IST. Bei Unternehmen ohne nennenswertes Anlagevermögen führt sie in die Irre — und Aktienrückkäufe verkleinern das Eigenkapital, wodurch das KBV mechanisch steigt, obwohl der Rückkauf dem Aktionär nützt. Apples Wert von rund 34 kommt genau daher.',
      },
      {
        begriff: 'Cashflow-Vielfaches',
        text: 'Börsenwert geteilt durch den operativen Cashflow — das Geld, das aus dem laufenden Geschäft tatsächlich hereinkommt.',
        taugt: 'Näher an der Wirklichkeit als der Gewinn, weil Abschreibungen und Bewertungsspielräume herausfallen. Bei Banken und Finanzierern taugt er wenig: Dort ist ein negativer operativer Cashflow normal, weil vergebene Kredite als Abfluss zählen.',
      },
      {
        begriff: 'PEG',
        text: 'KGV geteilt durch das Gewinnwachstum in Prozent. Peter Lynchs Faustregel: Unter 1 zahlt man für das gebotene Wachstum nicht zu viel.',
        taugt: 'Eine Momentaufnahme, keine Historie — und sie hängt ganz davon ab, welcher Zeitraum fürs Wachstum genommen wird. Bei einem Unternehmen, das aus einem schwachen Jahr heraus wächst, sieht das Wachstum riesig und das PEG verlockend aus.',
      },
      {
        begriff: 'FCF-Rendite',
        text: 'Freier Cashflow im Verhältnis zum Börsenwert, in Prozent. Was nach allen Investitionen übrig bleibt, gemessen am Preis.',
        taugt: 'Die ehrlichste der Preiskennzahlen: Übrig geblieben ist übrig geblieben. In einem Jahr mit großer Investition fällt sie niedrig aus, ohne dass das Unternehmen schlechter wäre. Bei Banken ist sie, wie das Cashflow-Vielfache, kaum zu gebrauchen.',
      },
      {
        begriff: 'Ertragswert (EPV)',
        text: 'Was das Unternehmen allein aus seiner heutigen Ertragskraft wert ist — geglätteter Gewinn geteilt durch die Kapitalkosten, ganz ohne Wachstumsannahme.',
        taugt: 'Eine nüchterne Untergrenze: So viel wäre es wert, wenn es nie wieder wüchse. Liegt der Kurs darunter, zahlt man für das laufende Geschäft weniger als es verdient. Bei Unternehmen, deren Wert fast ganz in künftigem Wachstum steckt, fällt er naturgemäß niedrig aus.',
      },
    ],
  },
  {
    titel: 'Die Fair-Wert-Linien in der Kursgrafik',
    einleitung:
      'Diese Linien zeigen, was der Titel wert wäre, wenn der Markt ihm wieder das Vielfache zugestünde, das er ihm in seiner eigenen Geschichte zugestanden hat. Sie sind keine Prognose, sondern ein Maßstab.',
    begriffe: [
      {
        begriff: 'Durchschnitts-Vielfaches (Ø-KGV, Ø-KUV …)',
        text: 'Der Median aller Vielfachen, die dieser Titel in den letzten elf Jahren hatte — gemessen an jedem einzelnen Handelstag, umgerechnet mit dem Wechselkurs von damals.',
        taugt: 'Median, nicht Mittelwert: Ein Jahr mit Einmalgewinn würde den Mittelwert in die Hunderte treiben. Gerechnet wird nur über vergleichbare Jahre — ab dem letzten großen Sprung in der Aktienzahl, denn ein Börsengang oder eine Fusion davor macht den Vergleich wertlos.',
      },
      {
        begriff: 'Stützpunkt',
        text: 'Die Punkte auf einer Fair-Wert-Linie. Dort sitzt eine gemeldete Zahl aus einem Geschäftsbericht.',
        taugt: 'Zwischen zwei Stützpunkten ist die Linie verbunden, nicht gemessen. Vor der ersten und nach der letzten gemeldeten Zahl verläuft sie waagerecht — es gibt dort nichts, wohin man verbinden könnte.',
      },
      {
        begriff: 'Übliche Spanne',
        text: 'Der Bereich, in dem ein Durchschnitts-Vielfaches glaubwürdig ist. Beim KGV 3 bis 60, beim KUV 0,2 bis 20, beim KBV 0,4 bis 15, beim Cashflow 1,5 bis 50, bei der Dividendenrendite 0,2 bis 8 %.',
        taugt: 'Gemessen über alle Titel, nicht geschätzt. Liegt ein Wert außerhalb, wird die Linie nicht versteckt, sondern gekennzeichnet — meist steckt dahinter ein Jahr mit fast null Gewinn und keine echte Bewertung.',
      },
      {
        begriff: 'Ø-Dividende',
        text: 'Fairer Kurs aus der Dividende der letzten zwölf Monate und der durchschnittlichen Dividendenrendite dieses Titels. Gemessen nur über die Jahre, in denen ohne Pause ausgeschüttet wurde, mindestens drei.',
        taugt: 'Passt zu Unternehmen mit verlässlicher Ausschüttung. Sonderdividenden zählen nicht mit, sonst erschiene jeder Titel nach einer Sonderzahlung billig. Nach einer Kürzung beginnt die Messung ein Jahr später neu, denn die alte Rendite gilt dann nicht mehr.',
      },
      {
        begriff: 'Potenzial bis zum fairen Wert',
        text: 'Wie weit der Kurs steigen müsste, um den fairen Wert zu erreichen. +25 % heißt: Der faire Wert liegt ein Viertel über dem Kurs, der Kurs also 20 % darunter. Ein negatives Potenzial heißt, der Kurs liegt über dem fairen Wert.',
        taugt: 'Steht im Screener und in der Liste „Unterbewertet“. Kein Kaufsignal: Günstig gegen die eigene Geschichte ist oft ein Geschäft, das sein altes Vielfache nicht mehr verdient. Ein Potenzial über 150 % deutet eher auf einen Bruch hin, etwa eine Fusion oder einen eingebrochenen Gewinn.',
      },
    ],
  },
  {
    titel: 'Marktlage',
    begriffe: [
      {
        begriff: 'Marktzustand',
        text: 'Eine Zahl von 0 bis 100 aus vier Bausteinen: Trend des S&P gegen seine 200-Tage-Linie (35 %), Kreditaufschlag für schwache Schuldner (25 %), Schwankungserwartung VIX (20 %) und Zinskurve (20 %).',
        taugt: 'Ein Zustandsmesser, kein Vorhersagegerät. Er sagt, dass die Bedingungen vorliegen, die historisch gute Phasen begleitet haben. An 98 von 100 Tagen ist das Urteil am nächsten Tag unverändert.',
      },
      {
        begriff: 'VIX',
        text: 'Die vom Optionsmarkt erwartete Schwankung der nächsten dreißig Tage.',
        taugt: 'Zählt im Marktzustand gegenläufig: Unter 15 gilt der Markt als auffällig ruhig, und auffällige Ruhe war historisch kein gutes Zeichen. Deshalb kann der VIX-Baustein negativ sein, während die Ampel grün steht.',
      },
      {
        begriff: 'Marktbreite',
        text: 'Der Anteil aller Titel, die über ihrer eigenen 200-Tage-Linie stehen. Jede Aktie hat eine Stimme — anders als im Index, wo die zehn größten rund ein Drittel ausmachen.',
        taugt: 'Liest sich nicht linear: Beide Enden gingen guten Renditen voraus, die Mitte war am schwächsten. Unter 30 % ist der Markt ausgewaschen, über 75 % breit getragen; dazwischen sagt die Zahl wenig.',
      },
      {
        begriff: 'Divergenz',
        text: 'Der Index steht nahe seinem Jahreshoch, aber weniger als die Hälfte der Titel trägt ihn.',
        taugt: 'Ein Aufmerksamkeitssignal, kein Verkaufssignal. Seit 2015 elfmal aufgetreten: dreimal folgte ein größerer Rückschlag, siebenmal nichts. Die drei großen Einbrüche des Jahrzehnts hatten allerdings alle dieses Muster vorher.',
      },
    ],
  },
  {
    titel: 'Qualität und Risiko',
    begriffe: [
      {
        begriff: 'ROIC',
        text: 'Rendite auf das investierte Kapital. Wie effizient ein Unternehmen aus eingesetztem Kapital Gewinn macht.',
        taugt: 'Die beste Einzelzahl für Unternehmensqualität. Dauerhaft über den Kapitalkosten heißt: Das Unternehmen schafft Wert, statt ihn nur umzuwälzen.',
      },
      {
        begriff: 'Piotroski-F-Score',
        text: 'Eine Neun-Punkte-Prüfung der Bilanzgesundheit: Profitabilität, Verschuldung, Effizienz. 8 bis 9 gilt als stark.',
        taugt: 'Prüft die Richtung, nicht das Niveau — ob sich die Lage gegenüber dem Vorjahr verbessert hat. Braucht mindestens zwei Jahre.',
      },
      {
        begriff: 'Drawdown',
        text: 'Der Rückgang vom letzten Höchststand, in Prozent.',
        taugt: 'Zeigt, wie tief es schon ging. Ein tiefer Drawdown ist für sich weder gut noch schlecht: Er kann eine Gelegenheit sein oder der Anfang.',
      },
      {
        begriff: 'Volatilität',
        text: 'Schwankungsbreite des Kurses, aufs Jahr gerechnet.',
        taugt: 'Begrenzt die empfohlene Positionsgröße: Bei gleichem Risikobudget darf von einem stark schwankenden Titel weniger ins Depot.',
      },
    ],
  },
  {
    titel: 'Qualyz-Begriffe',
    begriffe: [
      {
        begriff: 'Konviktion',
        text: 'Die Zahl der Linsen mit Kaufsignal, von 0 bis 7.',
        taugt: 'Je höher, desto mehr unabhängige Blickwinkel stimmen überein. Eine 7 bedeutet nicht "sicher", sondern "aus sieben verschiedenen Richtungen betrachtet auffällig".',
      },
      {
        begriff: 'Abdeckung',
        text: 'Wie viel von dem, was eine Linse braucht, tatsächlich vorliegt.',
        taugt: 'Ein fehlender Wert gibt keine Punkte und senkt die Abdeckung — das ist etwas anderes als ein schlechtes Urteil. Unter 50 % urteilt die Linse gar nicht.',
      },
      {
        begriff: 'Bewertungsbasis',
        text: 'Alle Je-Aktie-Werte werden aus Summen des Abschlusses und den tatsächlich gehandelten Einheiten gerechnet, nicht aus den gemeldeten Werten je Aktie.',
        taugt: 'Klingt nach Erbsenzählerei, ist aber der Unterschied zwischen 4,7 % und 68 % FCF-Rendite bei einem ausländischen Titel. Gemeldete Werte je Aktie wechseln bei Auslandstiteln die Einheit.',
      },
    ],
  },
]


/**
 * Die Punkteregeln kommen aus dem Backend (lenses/regeln.py) und sind dort
 * gegen die Linsen getestet. So steht im Glossar nie etwas anderes, als
 * gerechnet wird -- Kims Frage, warum es ab 8 % FCF-Rendite volle Punkte
 * gibt, hat genau hier gefehlt (14.09.2026).
 */
function Punkteregeln() {
  const regeln = useLinsenRegeln()
  if (regeln.isLoading) return <SkeletonZeilen anzahl={6} />
  if (!regeln.data) return null
  return (
    <>
      <p>
        Jede Linse verteilt 100 Punkte auf ihre Kriterien. Zwischen den beiden Schwellen steigen die Punkte
        gleichmäßig: Ein Wert genau dazwischen gibt die halbe Punktzahl. Fehlt ein Wert, gibt es dafür keine Punkte,
        und die Abdeckung der Linse sinkt — das ist etwas anderes als ein schlechtes Urteil.
      </p>
      {LENSES.map((linse) => {
        const kriterien = regeln.data.linsen[linse.key] ?? []
        if (kriterien.length === 0) return null
        return (
          <div key={linse.key} className="qa-regelblock">
            <h3 className="q-lensdef-title">
              {linse.label}
              <span className="q-lensdef-sub">{LINSEN_TEXT[linse.key].sub}</span>
            </h3>
            <div className="q-table-wrap">
              <table className="q-table qa-regeltabelle">
                <thead>
                  <tr>
                    <th scope="col">Kriterium</th>
                    <th scope="col">Punkte</th>
                    <th scope="col">Regel</th>
                  </tr>
                </thead>
                <tbody>
                  {kriterien.map((k) => (
                    <tr key={k.schluessel}>
                      <th scope="row">{k.name}</th>
                      <td>{k.max_punkte}</td>
                      <td className="qa-regeltext">{k.regel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {kriterien
              .filter((k) => k.erlaeuterung)
              .map((k) => (
                <p key={`erklaerung-${k.schluessel}`} className="qa-regel-erklaerung">
                  {k.erlaeuterung}
                </p>
              ))}
          </div>
        )
      })}
    </>
  )
}

export default function GlossarPage() {
  return (
    <div className="q-doc">
      <p className="q-doc-intro">
        Qualyz bewertet jede Aktie durch sieben unabhängige Linsen. Hier steht, was jede Linse misst, wie du die
        Bewertung liest und wie du die Seite nutzt.
      </p>

      <section className="q-sec">
        <h2 className="q-sec-title">So liest du eine Bewertung</h2>
        <p>
          Jede Linse vergibt 0 bis 100 Punkte und daraus ein Urteil. Die <b>Konviktion</b> ist die Zahl der Linsen
          mit Kaufsignal, also BUY oder STRONG. Sie ist die wichtigste Zahl: Sie sagt, wie viele unabhängige
          Blickwinkel zum selben Schluss kommen.
        </p>
        <div className="q-scale">
          <div className="q-vc q-vc--strong">
            <span className="q-vc-tag">STRONG</span>
            <div className="q-vc-rng">80 bis 100</div>
            <div className="q-vc-tx">Starkes Signal dieser Linse.</div>
          </div>
          <div className="q-vc q-vc--buy">
            <span className="q-vc-tag">BUY</span>
            <div className="q-vc-rng">65 bis 80</div>
            <div className="q-vc-tx">Klar positiv.</div>
          </div>
          <div className="q-vc q-vc--watch">
            <span className="q-vc-tag">WATCH</span>
            <div className="q-vc-rng">50 bis 65</div>
            <div className="q-vc-tx">Beobachten, noch kein Kauf.</div>
          </div>
          <div className="q-vc q-vc--pass">
            <span className="q-vc-tag">PASS</span>
            <div className="q-vc-rng">0 bis 50</div>
            <div className="q-vc-tx">Kein Kaufsignal dieser Linse.</div>
          </div>
        </div>
        <div className="q-note">
          <span className="q-note-mark" aria-hidden="true">
            ▨
          </span>
          <span>
            <b>Kein Signal</b> (schraffiert) ist etwas anderes als PASS: Der Linse fehlen die Daten für ein Urteil. Ein
            leeres Feld behauptet nichts, statt zu raten.
          </span>
        </div>
        <div className="q-note">
          <span className="q-note-mark" aria-hidden="true">
            %
          </span>
          <span>
            <b>Max. Position</b> ist keine Bewertung, sondern eine Größenempfehlung: wie viel Depotanteil ein Titel
            höchstens bekommen sollte, damit ein erwarteter Verlust dein Risikobudget nicht sprengt. Grundlage ist die{' '}
            <b>Verlustannahme</b>, der größere Wert aus bisherigem Drawdown und Volatilität.
          </span>
        </div>
      </section>

      <section className="q-sec">
        <h2 className="q-sec-title">Die sieben Linsen</h2>
        <div>
          {LENSES.map((linse) => {
            const t = LINSEN_TEXT[linse.key]
            return (
              <div key={linse.key} className="q-lensdef">
                <span className="q-lensdef-code" aria-hidden="true">
                  {linse.code}
                </span>
                <div>
                  <h3 className="q-lensdef-title">
                    {linse.label}
                    <span className="q-lensdef-sub">{t.sub}</span>
                  </h3>
                  <p className="q-lensdef-text">{t.text}</p>
                  <p className="q-lensdef-strong">
                    <b>STRONG</b> heißt: {t.strong}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className="q-sec">
        <h2 className="q-sec-title">So entstehen die Punkte</h2>
        <Punkteregeln />
      </section>

      {GRUPPEN.map((gruppe) => (
        <section className="q-sec" key={gruppe.titel}>
          <h2 className="q-sec-title">{gruppe.titel}</h2>
          {gruppe.einleitung && <p className="q-sec-intro">{gruppe.einleitung}</p>}
          <dl className="q-terms">
            {gruppe.begriffe.map((b) => (
              <div key={b.begriff} className="q-term">
                <dt>{b.begriff}</dt>
                <dd>
                  {b.text}
                  {b.taugt && <span className="q-term-taugt">{b.taugt}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <section className="q-sec">
        <h2 className="q-sec-title">So nutzt du Qualyz</h2>
        <ol className="q-steps">
          <li className="q-step">
            <span>
              <b>Regime prüfen.</b> Die Startseite zeigt, ob der Markt Aktien gerade begünstigt.
            </span>
          </li>
          <li className="q-step">
            <span>
              <b>Im Screener nach Konviktion filtern.</b> Mit hoher Konviktion beginnen und das Linsen-Band lesen:
              Welche Blickwinkel tragen den Titel?
            </span>
          </li>
          <li className="q-step">
            <span>
              <b>Die Form verstehen.</b> Ein breit grüner Titel ist etwas anderes als einer, den nur Momentum trägt.
              Beides kann passen, je nachdem, was du suchst.
            </span>
          </li>
          <li className="q-step">
            <span>
              <b>Die Position an der Empfehlung ausrichten.</b> Die max. Position sagt, wie groß ein Titel im Depot
              höchstens sein sollte.
            </span>
          </li>
          <li className="q-step">
            <span>
              <b>Merken und beobachten.</b> Mit dem Stern in die Watchlist legen. Die Konviktion verändert sich mit
              den Daten.
            </span>
          </li>
        </ol>
      </section>
    </div>
  )
}
